/**
 * Report Rendering Constants & Types (Story 5.13, Task 1.1)
 *
 * Single source of truth for all rendering constants, types, type guards,
 * formatting functions, and page-break logic shared between web preview
 * and PDF generation. Both apps/web and apps/server import from here.
 *
 * NEVER duplicate these values — any divergence breaks WYSIWYG guarantee.
 */

// ── Grid Constants ───────────────────────────────────────────────────

export const GRID_COLUMNS = 12;

/** Base row height unit: web uses px, PDF uses pt (80px × 0.75 = 60pt) */
export const ROW_HEIGHT_UNIT_PX = 80;
export const ROW_HEIGHT_UNIT_PT = 60; // Corrected from 50pt (Story 5.13)

/** Grid gap: web uses px, PDF uses pt (16px × 0.75 = 12pt) */
export const GRID_GAP_PX = 16;
export const GRID_GAP_PT = 12; // Corrected from 8pt (Story 5.13)

/** A4 dimensions in points (1pt = 1/72 inch) */
export const A4_WIDTH_PT = 595.28;
export const A4_HEIGHT_PT = 841.89;

/** Margins in points */
export const MARGIN_TOP_PT = 72; // 1 inch
export const MARGIN_BOTTOM_PT = 72; // 1 inch
export const MARGIN_SIDES_PT = 54; // 0.75 inch

/** Content area dimensions in points */
export const CONTENT_WIDTH_PT = A4_WIDTH_PT - MARGIN_SIDES_PT * 2; // ~487pt
export const CONTENT_HEIGHT_PT =
  A4_HEIGHT_PT - MARGIN_TOP_PT - MARGIN_BOTTOM_PT; // ~698pt

/** Column width in points */
export const COL_WIDTH_PT =
  (CONTENT_WIDTH_PT - GRID_GAP_PT * (GRID_COLUMNS - 1)) / GRID_COLUMNS;

/** A4 usable content in pixels (for web preview) */
export const A4_CONTENT_WIDTH_PX = 649;
export const A4_CONTENT_HEIGHT_PX = 931;

// ── Report Format Type (Story 5.23) ─────────────────────────────────

export type ReportFormat = "document" | "slide-deck";

// ── Page Categories (Story 5.37) ────────────────────────────────────

export interface PageCategory {
  id: string;
  label: string;
  iconName: string;
  order: number;
}

export const PAGE_CATEGORIES: readonly PageCategory[] = [
  { id: "summary", label: "Summary", iconName: "SquaresFour", order: 0 },
  { id: "audience", label: "Audience", iconName: "Users", order: 1 },
  { id: "acquisition", label: "Acquisition", iconName: "CursorClick", order: 2 },
  { id: "engagement", label: "Engagement", iconName: "ChartLineUp", order: 3 },
  { id: "conversions", label: "Conversions", iconName: "Target", order: 4 },
  { id: "revenue", label: "Revenue", iconName: "CurrencyDollar", order: 5 },
  { id: "seo", label: "SEO", iconName: "MagnifyingGlass", order: 6 },
  { id: "social", label: "Social Media", iconName: "ShareNetwork", order: 7 },
  { id: "email", label: "Email & SMS", iconName: "EnvelopeSimple", order: 8 },
  { id: "calls", label: "Call Tracking", iconName: "Phone", order: 9 },
  { id: "custom", label: "Custom", iconName: "Notebook", order: 10 },
] as const;

export const DEFAULT_CATEGORY = "summary";

export const PAGE_CATEGORY_IDS = new Set(PAGE_CATEGORIES.map((c) => c.id));

// ── Multi-Page Types (Story 5.32) ───────────────────────────────────

/** Minimal widget shape for ReportPage (compatible with both web and server WidgetConfig) */
export interface ReportPageWidget {
  id: string;
  type: string;
  title: string;
  position: { row: number; col: number; colSpan: number; rowSpan?: number };
  config: Record<string, unknown>;
  autoSize?: boolean;
}

/** A single page within a multi-page report */
export interface ReportPage {
  id: string;       // crypto.randomUUID()
  title: string;    // max 200 chars, validated with HTML_INJECTION_PATTERN, min 1 char
  order: number;    // sort key, contiguous 0-based after normalization
  category?: string; // page category ID from PAGE_CATEGORIES, defaults to "summary"
  widgets: ReportPageWidget[];
}

/** Multi-page layout structure — replaces flat TemplateLayout for reports */
export interface ReportPagesLayout {
  version: 1;
  grid: { columns: 12 };
  pages: ReportPage[];
  format?: ReportFormat;
}

/** Multi-page limits */
export const MAX_PAGES = 20;
export const MAX_WIDGETS_TOTAL = 200;
export const MAX_PAGE_TITLE_LENGTH = 200;
export const MAX_LAYOUT_JSON_BYTES = 900_000;

// ── Slide Deck Constants (True 16:9) ─────────────────────────────────
// 960×540 fills 16:9 monitors completely (unlike A4 landscape at 1.41:1).
// 1:1 scale factor (960px web = 960pt PDF) eliminates WYSIWYG conversion bugs.

/** Slide deck page dimensions in points (custom page, NOT A4 landscape) */
export const SLIDE_PDF_WIDTH_PT = 960;
export const SLIDE_PDF_HEIGHT_PT = 540;

/** Slide deck web dimensions in pixels */
export const SLIDE_WIDTH_PX = 960;
export const SLIDE_HEIGHT_PX = 540;

/** Slide deck margins (tighter than portrait for slide feel) */
export const SLIDE_MARGIN_PT = 36; // all sides

/** Slide deck content area */
export const SLIDE_CONTENT_WIDTH_PT = SLIDE_PDF_WIDTH_PT - SLIDE_MARGIN_PT * 2; // 888pt
export const SLIDE_CONTENT_HEIGHT_PT = SLIDE_PDF_HEIGHT_PT - SLIDE_MARGIN_PT * 2; // 468pt
export const SLIDE_CONTENT_WIDTH_PX = 888; // 1:1 with PT
export const SLIDE_CONTENT_HEIGHT_PX = 468; // 1:1 with PT

/** Slide deck row height (1:1 scale) */
export const SLIDE_ROW_HEIGHT_UNIT_PX = 54;
export const SLIDE_ROW_HEIGHT_UNIT_PT = 54;

/** Slide deck gap (1:1 scale) */
export const SLIDE_GAP_PX = 12;
export const SLIDE_GAP_PT = 12;

/** Slide deck column width: (888 - 12*11) / 12 = 63pt */
export const SLIDE_COL_WIDTH_PT =
  (SLIDE_CONTENT_WIDTH_PT - SLIDE_GAP_PT * (GRID_COLUMNS - 1)) / GRID_COLUMNS;

/** Rows per slide: floor((468 + 12) / (54 + 12)) = 7 */
export const SLIDE_ROWS_PER_PAGE = Math.floor(
  (SLIDE_CONTENT_HEIGHT_PX + SLIDE_GAP_PX) / (SLIDE_ROW_HEIGHT_UNIT_PX + SLIDE_GAP_PX)
);

// ── Format-Aware Grid Dimensions ─────────────────────────────────────

export interface GridDimensions {
  contentWidthPx: number;
  contentHeightPx: number;
  contentWidthPt: number;
  contentHeightPt: number;
  rowHeightPx: number;
  rowHeightPt: number;
  gapPx: number;
  gapPt: number;
  marginPt: number;
  colWidthPt: number;
  pageWidthPt: number;
  pageHeightPt: number;
}

/**
 * Returns all format-specific grid constants.
 * Call with "document" for A4 portrait, "slide-deck" for 16:9 landscape.
 */
export function getGridDimensions(format: ReportFormat): GridDimensions {
  if (format === "slide-deck") {
    return {
      contentWidthPx: SLIDE_CONTENT_WIDTH_PX,
      contentHeightPx: SLIDE_CONTENT_HEIGHT_PX,
      contentWidthPt: SLIDE_CONTENT_WIDTH_PT,
      contentHeightPt: SLIDE_CONTENT_HEIGHT_PT,
      rowHeightPx: SLIDE_ROW_HEIGHT_UNIT_PX,
      rowHeightPt: SLIDE_ROW_HEIGHT_UNIT_PT,
      gapPx: SLIDE_GAP_PX,
      gapPt: SLIDE_GAP_PT,
      marginPt: SLIDE_MARGIN_PT,
      colWidthPt: SLIDE_COL_WIDTH_PT,
      pageWidthPt: SLIDE_PDF_WIDTH_PT,
      pageHeightPt: SLIDE_PDF_HEIGHT_PT,
    };
  }

  // Default: document (A4 portrait)
  return {
    contentWidthPx: A4_CONTENT_WIDTH_PX,
    contentHeightPx: A4_CONTENT_HEIGHT_PX,
    contentWidthPt: CONTENT_WIDTH_PT,
    contentHeightPt: CONTENT_HEIGHT_PT,
    rowHeightPx: ROW_HEIGHT_UNIT_PX,
    rowHeightPt: ROW_HEIGHT_UNIT_PT,
    gapPx: GRID_GAP_PX,
    gapPt: GRID_GAP_PT,
    marginPt: MARGIN_SIDES_PT,
    colWidthPt: COL_WIDTH_PT,
    pageWidthPt: A4_WIDTH_PT,
    pageHeightPt: A4_HEIGHT_PT,
  };
}

// ── Chart Color Constants ─────────────────────────────────────────────

/** Brand chart series colors — used identically in web and PDF */
export const CHART_COLORS = [
  "#FF6D6F", // Brand coral (series 1)
  "#3B82F6", // Blue (series 2)
  "#22C55E", // Green (series 3)
  "#F59E0B", // Amber (series 4)
  "#8B5CF6", // Purple (series 5)
  "#EC4899", // Pink (series 6)
] as const;

/** Trend indicator colors — used identically in web and PDF */
export const TREND_COLORS = {
  up: "#22C55E",
  down: "#EF4444",
  neutral: "#737373",
} as const;

// ── Annotation Constants ─────────────────────────────────────────────

/** Annotation line color presets — distinct from CHART_COLORS to avoid visual confusion */
export const ANNOTATION_COLORS = {
  blue: "#3B82F6",
  green: "#22C55E",
  orange: "#F59E0B",
  red: "#EF4444",
  gray: "#737373",
} as const;

export type AnnotationColorKey = keyof typeof ANNOTATION_COLORS;

/** Ordered list of annotation color options for UI selects */
export const ANNOTATION_COLOR_OPTIONS = Object.keys(ANNOTATION_COLORS) as AnnotationColorKey[];

/** Annotation icon presets — maps to Lucide icon component names */
export const ANNOTATION_ICONS = ["rocket", "dollar", "alert", "flag"] as const;

export type AnnotationIconKey = (typeof ANNOTATION_ICONS)[number];

/** Ordered list of annotation icon options for UI selects */
export const ANNOTATION_ICON_OPTIONS = [...ANNOTATION_ICONS] as const;

/** Chart-agnostic annotation config — reusable for bar charts in future stories */
export interface AnnotationConfig {
  id: string;
  date: string;
  label: string;
  description?: string;
  color: AnnotationColorKey;
  icon?: AnnotationIconKey;
}

// ── Widget Types ─────────────────────────────────────────────────────

// SYNC: this list MUST stay in lock-step with `WIDGET_TYPES` in
// packages/db/src/schema/report-layout-types.ts (the two packages cannot share
// a const due to db's rootDir constraint). Adding/removing a widget type here
// requires the same edit there — plus a `WIDGET_CONFIG_SCHEMAS` entry on the db
// side. See Story 37.1 Dev Notes → "Gauge: the second allowlist".
export const KNOWN_WIDGET_TYPES = [
  "kpi",
  "line-chart",
  "bar-chart",
  "pie-chart",
  "data-table",
  "text-block",
  "top-posts",
  "cover-page",
  "funnel",
  "demographic",
  "heading",
  "paragraph",
  "rich-text",
  "spacer-divider",
  "image",
  "embed",
  "custom-metric",
  "benchmark",
  "goal",
  "gauge",
  "rollup-table", // Story 40-5 — per-client rows + totals/averages footer (roll-up only)
] as const;

export type KnownWidgetType = (typeof KNOWN_WIDGET_TYPES)[number];

// ── Cover Page Background Styles ─────────────────────────────────────

/** Background style presets for cover page widget (Story 5.16) */
export const BACKGROUND_STYLE_OPTIONS = [
  { value: "gradient-blue", label: "Gradient Blue" },
  { value: "gradient-dark", label: "Gradient Dark" },
  { value: "solid-white", label: "Solid White" },
  { value: "solid-dark", label: "Solid Dark" },
  { value: "gradient-green", label: "Gradient Green" },
  { value: "gradient-purple", label: "Gradient Purple" },
] as const;

export type BackgroundStyleValue = (typeof BACKGROUND_STYLE_OPTIONS)[number]["value"];

// ── Widget Data Types ────────────────────────────────────────────────

/**
 * Story 32-7 — fetch-time account-contribution metadata for multi-account
 * aggregates. Present ONLY when the widget consulted ≥2 accounts and at
 * least one failed to contribute (absence ⇔ no warning). Computed by the
 * server from per-integration fetch OUTCOMES — never from config-time
 * attachment counts — so it reflects the current scope/health state.
 */
export interface WidgetAccountContribution {
  /** Accounts whose fetch succeeded and whose records entered this widget's aggregate. */
  contributed: number;
  /** Accounts this widget consulted at fetch time (non-paused candidates ∩ the widget's optional integrationIds selection). */
  consulted: number;
  /** Per-platform breakdown — cross-platform widgets (top-posts "all", multi-platform line charts) aggregate several platforms. */
  byPlatform: Array<{ platform: string; contributed: number; consulted: number }>;
  /**
   * Story 40-4 — per-CLIENT breakdown, populated ONLY by the roll-up fan-out
   * (`populateRollupWidgets`): which member clients contributed records to this
   * widget's cross-client aggregate. Undefined for single-client populate.
   * Feeds the Roll-up Table drill-down (40-5). KEEP IN SYNC with the server
   * mirror in apps/server/src/services/widget-data-transformer.ts.
   */
  byClient?: Array<{ clientId: string; contributed: number; consulted: number }>;
}

// SYNC (Story 32-7): the server keeps a LOCAL mirror of this envelope (and
// of WidgetAccountContribution) in
// apps/server/src/services/widget-data-transformer.ts — editing the shape
// here requires the same edit there, or the field silently drops/blocks at
// the server boundary (same trap class as the widget-type triple allowlist).
export interface WidgetDataEnvelope<T> {
  loading: boolean;
  error: string | null;
  data: T | null;
  /**
   * Story 32-7 — present iff the widget's aggregate was built from only a
   * SUBSET of the accounts it consulted at fetch time (server attach rule:
   * error === null && consulted ≥ 2 && contributed < consulted). Absent on
   * error envelopes, single-account widgets, and fully-contributing
   * aggregates — absence IS the no-warning signal.
   */
  accountContribution?: WidgetAccountContribution;
}

/**
 * Story 8.11 — inline industry-benchmark indicator on a report KPI card.
 *
 * Attached to `KpiData` (server populate path) ONLY when every gate passes:
 * config.showBenchmark === true, the KPI metric maps to one of the 6
 * benchmarkable metric_names, the platform is benchmarkable, the client's
 * free-form industry normalizes, a benchmark_data row exists, AND the tenant
 * plan is Starter+. Absent ⇒ no indicator (graceful) / Free plan (web shows the
 * upgrade CTA from the showBenchmark flag instead).
 *
 * `badge` reuses the SAME lower-is-better classification as `transformBenchmark`
 * (for cpc/cpa/bounce_rate, below median = 'above' = good).
 */
export interface BenchmarkComparison {
  /** Industry median for the metric (display: "Industry avg: {median}"). */
  median: number;
  p25: number;
  p75: number;
  /** Relative-performance verdict (lower-is-better aware). */
  badge: "above" | "average" | "below";
  /** Unit for value/median formatting — matches the standalone benchmark widget. */
  unit: "%" | "$" | "seconds";
  source: string;
  sourceUrl: string | null;
  effectiveYear: number;
  lastUpdated: number;
}

export interface KpiData {
  value: number;
  previousValue?: number;
  format?: "number" | "currency" | "percentage";
  /** Optional recent history for the in-card sparkline (oldest → newest, the last
   *  point being the current value). Rendered as a tiny trend line under the value
   *  on the canvas and in the PDF. Absent ⇒ no sparkline (e.g. live data with no
   *  series yet). */
  sparkline?: number[];
  /**
   * Story 8.11 — present iff the inline industry-benchmark indicator resolved
   * (all gates passed, see BenchmarkComparison). Absent ⇒ no indicator. The
   * field is part of the TRIPLICATED KpiData envelope: this canonical copy +
   * the server local mirror in
   * apps/server/src/services/widget-data-transformer.ts. Editing the shape here
   * requires the same edit there, or the field silently drops at the boundary.
   */
  benchmarkComparison?: BenchmarkComparison;
}

export interface TimeSeriesData {
  points: Array<{ date: string; [metricKey: string]: number | string }>;
  comparisonPoints?: Array<{
    date: string;
    [metricKey: string]: number | string;
  }>;
}

export interface CategoricalData {
  categories: Array<Record<string, string | number>>;
  comparisonCategories?: Array<Record<string, string | number>>;
}

export interface DistributionData {
  slices: Array<{ name: string; value: number }>;
}

export interface TabularData {
  rows: Array<Record<string, string | number>>;
  totalRows?: number;
}

// ── Top Posts Widget Types (Story 5.15) ──────────────────────────────

export interface TopPostRecord {
  postId: string;
  platform: "facebook-pages" | "instagram-business" | "linkedin-pages";
  postType:
    | "image"
    | "video"
    | "link"
    | "text"
    | "carousel"
    | "reel"
    | "unknown";
  content: string;
  createdAt: string;
  thumbnailUrl: string | null;
  thumbnailBase64: string | null;
  permalinkUrl: string;
  reactions: number;
  comments: number;
  shares: number | null;
  saves: number | null;
  impressions: number;
  clicks: number;
  engagementTotal: number;
}

export interface TopPostsData {
  posts: TopPostRecord[];
}

// ── Funnel Widget Types (Story 5.18) ──────────────────────────────────

export interface FunnelStageData {
  label: string;
  value: number;
}

export interface FunnelData {
  stages: FunnelStageData[];
  comparisonStages?: FunnelStageData[];
}

// ── Demographic Widget Types (Story 5.19) ────────────────────────────

export interface DemographicSlice {
  name: string;
  value: number;
  percentage: number;
  secondaryMetrics?: Record<string, number>;
}

export interface DemographicCategory {
  bracket: string;
  segments: Array<{ name: string; value: number }>;
}

export interface DemographicData {
  mode:
    | "device-breakdown"
    | "new-vs-returning"
    | "geographic-regions"
    | "age-gender";
  slices?: DemographicSlice[];
  categories?: DemographicCategory[];
  totalSessions: number;
  comparisonData?: {
    slices?: DemographicSlice[];
    categories?: DemographicCategory[];
    totalSessions: number;
  };
  unavailable?: {
    reason: "google_signals_required" | "data_thresholded" | "no_data";
    helpUrl?: string;
  };
  disclaimer?: string;
}

// ── Benchmark Widget Types (Story 5.29) ──────────────────────────────

export interface BenchmarkItem {
  metricName: string;
  clientValue: number;
  median: number;
  p25: number;
  p75: number;
  unit: "%" | "$" | "seconds";
  platform: string;
  source: string;
  sourceUrl: string | null;
  effectiveYear: number;
  lastUpdated: number;
  badge: "above" | "average" | "below";
}

export interface BenchmarkDataQuality {
  availableMetrics: string[];
  staleMetrics: string[];
  missingMetrics: string[];
  lastSync: string | null;
}

export interface BenchmarkData {
  summary: {
    above: number;
    average: number;
    below: number;
    total: number;
  };
  items: BenchmarkItem[];
  dataQuality: BenchmarkDataQuality;
}

// ── Goal Widget Types (Story 5.30) ────────────────────────────────────

export type GoalStatus = "on-track" | "warning" | "danger" | "achieved" | "exceeded";

export interface GoalPacingData {
  projectedValue: number;
  isOnTrack: boolean;
  daysRemaining: number;
  daysElapsed: number;
  totalDays: number;
}

export interface GoalData {
  currentValue: number;
  targetValue: number;
  percentage: number;
  unit: "currency" | "number" | "percentage";
  currencyCode: string;
  pacing: GoalPacingData | null;
  status: GoalStatus;
  goalType: "budget" | "metric" | "custom";
  metricLabel: string | null;
}

export type AnyWidgetPayload =
  | KpiData
  | TimeSeriesData
  | CategoricalData
  | DistributionData
  | TabularData
  | TopPostsData
  | FunnelData
  | DemographicData
  | BenchmarkData
  | GoalData;

export type WidgetData = WidgetDataEnvelope<AnyWidgetPayload>;

// ── Type Guards ──────────────────────────────────────────────────────

export function isKpiData(data: unknown): data is KpiData {
  if (!data || typeof data !== "object") return false;
  const d = data as Record<string, unknown>;
  return (
    typeof d.value === "number" &&
    !Array.isArray(d.points) &&
    !Array.isArray(d.categories) &&
    !Array.isArray(d.slices) &&
    !Array.isArray(d.rows)
  );
}

export function isTimeSeriesData(data: unknown): data is TimeSeriesData {
  if (!data || typeof data !== "object") return false;
  const d = data as Record<string, unknown>;
  return Array.isArray(d.points) && !Array.isArray(d.categories);
}

export function isCategoricalData(data: unknown): data is CategoricalData {
  if (!data || typeof data !== "object") return false;
  const d = data as Record<string, unknown>;
  return Array.isArray(d.categories) && !Array.isArray(d.points);
}

export function isDistributionData(data: unknown): data is DistributionData {
  if (!data || typeof data !== "object") return false;
  const d = data as Record<string, unknown>;
  return (
    Array.isArray(d.slices) &&
    !Array.isArray(d.rows) &&
    // Exclude DemographicData which also has slices but has mode + totalSessions
    typeof d.mode !== "string" &&
    d.slices.every(
      (s: unknown) =>
        typeof s === "object" &&
        s !== null &&
        typeof (s as Record<string, unknown>).name === "string" &&
        typeof (s as Record<string, unknown>).value === "number"
    )
  );
}

export function isTabularData(data: unknown): data is TabularData {
  if (!data || typeof data !== "object") return false;
  const d = data as Record<string, unknown>;
  return Array.isArray(d.rows) && !Array.isArray(d.slices);
}

const VALID_POST_PLATFORMS = new Set(["facebook-pages", "instagram-business", "linkedin-pages"]);

export function isTopPostsData(data: unknown): data is TopPostsData {
  if (!data || typeof data !== "object") return false;
  const d = data as Record<string, unknown>;
  return (
    Array.isArray(d.posts) &&
    d.posts.every((p: unknown) => {
      if (typeof p !== "object" || p === null) return false;
      const r = p as Record<string, unknown>;
      return (
        typeof r.postId === "string" &&
        typeof r.platform === "string" &&
        VALID_POST_PLATFORMS.has(r.platform) &&
        typeof r.reactions === "number" &&
        typeof r.comments === "number" &&
        typeof r.impressions === "number"
      );
    })
  );
}

export function isFunnelData(data: unknown): data is FunnelData {
  if (!data || typeof data !== "object") return false;
  const d = data as Record<string, unknown>;
  return (
    Array.isArray(d.stages) &&
    d.stages.length >= 2 &&
    d.stages.every(
      (s: unknown) =>
        typeof s === "object" &&
        s !== null &&
        typeof (s as Record<string, unknown>).label === "string" &&
        typeof (s as Record<string, unknown>).value === "number"
    )
  );
}

const DEMOGRAPHIC_MODES = new Set([
  "device-breakdown",
  "new-vs-returning",
  "geographic-regions",
  "age-gender",
]);

export function isDemographicData(data: unknown): data is DemographicData {
  if (!data || typeof data !== "object") return false;
  const d = data as Record<string, unknown>;
  return (
    typeof d.mode === "string" &&
    DEMOGRAPHIC_MODES.has(d.mode) &&
    typeof d.totalSessions === "number"
  );
}

export function isBenchmarkData(data: unknown): data is BenchmarkData {
  if (!data || typeof data !== "object") return false;
  const d = data as Record<string, unknown>;
  return (
    typeof d.summary === "object" &&
    d.summary !== null &&
    Array.isArray(d.items) &&
    typeof d.dataQuality === "object" &&
    d.dataQuality !== null &&
    typeof (d.summary as Record<string, unknown>).above === "number" &&
    typeof (d.summary as Record<string, unknown>).below === "number"
  );
}

const VALID_GOAL_STATUSES = new Set(["on-track", "warning", "danger", "achieved", "exceeded"]);
const VALID_GOAL_TYPES = new Set(["budget", "metric", "custom"]);

export function isGoalData(data: unknown): data is GoalData {
  if (!data || typeof data !== "object") return false;
  const d = data as Record<string, unknown>;
  return (
    typeof d.currentValue === "number" &&
    typeof d.targetValue === "number" &&
    typeof d.percentage === "number" &&
    typeof d.status === "string" &&
    VALID_GOAL_STATUSES.has(d.status) &&
    typeof d.goalType === "string" &&
    VALID_GOAL_TYPES.has(d.goalType)
  );
}

// ── Funnel Calculation Utilities (Story 5.18) ─────────────────────────

/** Retention color thresholds per AC #3 */
export const RETENTION_COLORS = {
  good: "#22C55E", // >70% retention (green)
  warning: "#F59E0B", // 40–70% retention (yellow)
  poor: "#EF4444", // <40% retention (red)
} as const;

/**
 * Returns the semantic hex color for a given retention rate.
 * Retention = (currentValue / previousValue) * 100
 */
export function getRetentionColor(retentionRate: number): string {
  if (retentionRate > 70) return RETENTION_COLORS.good;
  if (retentionRate >= 40) return RETENTION_COLORS.warning;
  return RETENTION_COLORS.poor;
}

export interface FunnelStageMetrics {
  label: string;
  value: number;
  conversionRate: number; // percentage of first stage (overall)
  dropOffRate: number; // percentage lost from previous stage
  retentionRate: number; // percentage retained from previous stage
  retentionColor: string; // hex color based on retention threshold
}

/**
 * Calculates funnel metrics for each stage: conversion rate, drop-off rate,
 * retention rate, and retention color. First stage has 100% conversion,
 * 0% drop-off, and good retention color.
 */
export function calculateFunnelMetrics(
  stages: FunnelStageData[]
): FunnelStageMetrics[] {
  if (stages.length === 0) return [];

  const firstValue = stages[0]!.value;

  return stages.map((stage, index) => {
    if (index === 0) {
      return {
        label: stage.label,
        value: stage.value,
        conversionRate: 100,
        dropOffRate: 0,
        retentionRate: 100,
        retentionColor: RETENTION_COLORS.good,
      };
    }

    const prevValue = stages[index - 1]!.value;
    const retentionRate = prevValue > 0 ? (stage.value / prevValue) * 100 : 0;
    const dropOffRate = 100 - retentionRate;
    const conversionRate = firstValue > 0 ? (stage.value / firstValue) * 100 : 0;

    return {
      label: stage.label,
      value: stage.value,
      conversionRate,
      dropOffRate,
      retentionRate,
      retentionColor: getRetentionColor(retentionRate),
    };
  });
}

// ── Envelope Helpers ─────────────────────────────────────────────────

export function createLoadingEnvelope<T>(): WidgetDataEnvelope<T> {
  return { loading: true, error: null, data: null };
}

export function createErrorEnvelope<T>(error: string): WidgetDataEnvelope<T> {
  return { loading: false, error, data: null };
}

export function createDataEnvelope<T>(data: T): WidgetDataEnvelope<T> {
  return { loading: false, error: null, data };
}

// ── KPI Formatting Functions ─────────────────────────────────────────

/**
 * Deterministic font size for KPI values based on character length and colSpan.
 * Same inputs always produce the same output — used in both web and PDF.
 */
export function getKpiFontSize(value: string, colSpan: number): number {
  const charCount = value.length;
  const maxSize =
    colSpan >= 6 ? 36 : colSpan >= 4 ? 28 : colSpan >= 3 ? 24 : 18;
  const minSize = colSpan <= 2 ? 14 : 16;
  const preferred = maxSize - Math.max(0, charCount - 5) * 2;
  return Math.max(minSize, Math.min(preferred, maxSize));
}

/**
 * Format a KPI value with pinned en-US locale for deterministic output.
 * Uses Intl.NumberFormat with compact notation for values >= 1M.
 * Identical output in both web preview and PDF generation.
 */
export function formatKpiValue(
  value: number,
  format?: KpiData["format"]
): string {
  const isLarge = Math.abs(value) >= 1_000_000;

  if (format === "percentage") {
    return new Intl.NumberFormat("en-US", {
      style: "percent",
      maximumFractionDigits: 1,
    }).format(value / 100);
  }

  if (format === "currency") {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      notation: isLarge ? "compact" : "standard",
      maximumFractionDigits: isLarge ? 1 : 0,
    }).format(value);
  }

  return new Intl.NumberFormat("en-US", {
    notation: isLarge ? "compact" : "standard",
    maximumFractionDigits: 1,
  }).format(value);
}

/**
 * Calculate trend direction and percentage change.
 * Uses Math.abs(previous) for correct sign with negative previous values.
 * When previous === 0, returns percentage=0 (not "N/A").
 */
export function calculateTrend(
  current: number,
  previous: number
): { direction: "up" | "down" | "neutral"; percentage: number } {
  if (previous === 0)
    return { direction: current > 0 ? "up" : "neutral", percentage: 0 };
  const change = ((current - previous) / Math.abs(previous)) * 100;
  if (Math.abs(change) < 0.1) return { direction: "neutral", percentage: 0 };
  return {
    direction: change > 0 ? "up" : "down",
    percentage: Math.abs(change),
  };
}

// ── Benchmark Formatting Functions (Story 5.29) ──────────────────────

export type BenchmarkMetricUnit = "%" | "$" | "seconds";

/**
 * Format a benchmark metric value with its unit.
 * Shared between web preview and PDF generation for WYSIWYG consistency.
 */
export function formatBenchmarkMetricValue(value: number, unit: BenchmarkMetricUnit): string {
  if (unit === "%") return `${value.toFixed(1)}%`;
  if (unit === "$") return `$${value.toFixed(2)}`;
  if (unit === "seconds") {
    const mins = Math.floor(value / 60);
    const secs = Math.round(value % 60);
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  }
  return String(value);
}

// ── Page Break Algorithm ─────────────────────────────────────────────

/** Minimal widget position shape for page break calculation */
export interface PageBreakWidgetPosition {
  row: number;
  col: number;
  colSpan: number;
  rowSpan?: number;
}

export interface PageBreakWidget {
  id: string;
  position: PageBreakWidgetPosition;
}

/**
 * Calculates page break positions based on cumulative row heights.
 * Returns row indices where page breaks should occur (before that row).
 *
 * Parameterized by unit system — call with px values for web, pt for PDF.
 * Same algorithm, same break positions when proportionally scaled.
 *
 * @param widgets - All widgets in the canvas
 * @param pageHeight - Usable content height (931px for web, 698pt for PDF)
 * @param rowUnit - Height of 1 row unit (80px for web, 60pt for PDF)
 * @param gap - Gap between rows (16px for web, 12pt for PDF)
 */
export function calculatePageBreaks(
  widgets: PageBreakWidget[],
  pageHeight: number,
  rowUnit: number,
  gap: number
): number[] {
  if (widgets.length === 0) return [];

  const maxRow = Math.max(
    ...widgets.map((w) => w.position.row + (w.position.rowSpan ?? 1))
  );

  const breaks: number[] = [];
  let cumulativeHeight = 0;

  for (let row = 0; row < maxRow; row++) {
    const rowHeight = rowUnit + (row > 0 ? gap : 0);

    if (cumulativeHeight + rowHeight > pageHeight && cumulativeHeight > 0) {
      breaks.push(row);
      cumulativeHeight = rowUnit; // reset for new page
    } else {
      cumulativeHeight += rowHeight;
    }
  }

  return breaks;
}
